import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, Compass } from "lucide-react";
import { StayHeader } from "@/components/stays/StayHeader";
import { PropertyCard } from "@/components/stays/PropertyCard";
import { MobileBottomNav } from "@/components/stays/MobileBottomNav";
import { GridSkeleton } from "@/components/stays/Skeletons";
import Footer from "@/components/Footer";
import { getProperties, getMapPoints } from "@/lib/stayflowApi";
import { useWishlist } from "@/lib/wishlist";
import { setMeta, SITE_URL } from "@/lib/seo";

const Wishlist = () => {
  const ids = useWishlist();
  const { data, isLoading } = useQuery({ queryKey: ["properties"], queryFn: getProperties, staleTime: 5 * 60 * 1000 });
  const mapQ = useQuery({ queryKey: ["map-points"], queryFn: getMapPoints, staleTime: 10 * 60 * 1000 });
  const geoById = React.useMemo(() => Object.fromEntries((mapQ.data || []).map((g) => [g.id, g])), [mapQ.data]);

  useEffect(() => { setMeta({ title: "Your Wishlist — StayFlow", description: "Stays you've saved on StayFlow.", url: `${SITE_URL}/wishlist` }); }, []);

  const saved = React.useMemo(() => {
    const all = Array.isArray(data) ? data : [];
    const order = new Map(ids.map((id, i) => [id, i]));
    return all.filter((p) => order.has(p.id)).sort((a, b) => order.get(a.id) - order.get(b.id));
  }, [data, ids]);

  return (
    <div className="bg-paper min-h-screen pb-24 md:pb-0" data-testid="wishlist-page">
      <StayHeader className="hidden md:block" />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex items-center gap-2 text-brand">
          <Heart className="w-4 h-4 fill-current" aria-hidden="true" />
          <p className="overline">Saved stays</p>
        </div>
        <h1 className="mt-3 font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-ink">Your wishlist</h1>
        <p className="mt-3 text-ink-soft">{saved.length} stay{saved.length !== 1 ? "s" : ""} saved on this device.</p>

        <div className="mt-8">
          {isLoading && ids.length > 0 && <GridSkeleton count={3} />}

          {!isLoading && saved.length === 0 && (
            <div className="rounded-3xl border border-black/8 bg-white p-16 text-center" data-testid="wishlist-empty">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-soft text-brand flex items-center justify-center">
                <Heart className="w-6 h-6" aria-hidden="true" />
              </div>
              <p className="mt-5 font-heading font-bold text-xl text-ink">Your wishlist is empty</p>
              <p className="mt-2 text-ink-soft max-w-sm mx-auto">Tap the heart on any stay to save it here for later.</p>
              <Link to="/stays" data-testid="wishlist-explore" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                <Compass className="w-4 h-4" /> Explore stays
              </Link>
            </div>
          )}

          {saved.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-8" data-testid="wishlist-grid">
              {saved.map((p, i) => <PropertyCard key={p.id} property={p} index={i} locationOverride={geoById[p.id]} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default Wishlist;
